import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { Wrench, Clock } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { fetchMaintenance } from "@/lib/announcements";

/** Routes that stay reachable during maintenance so staff can sign in. */
const ALLOWED_PREFIXES = ["/login"];

function formatRemaining(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function Countdown({ endsAt }: { endsAt: string }) {
  const target = new Date(endsAt).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (Number.isNaN(target)) return null;
  const remaining = target - now;

  return (
    <div className="mt-6 rounded-xl border border-border bg-card px-4 py-3">
      <p className="flex items-center justify-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Clock className="h-3.5 w-3.5" /> {remaining > 0 ? "Expected back in" : "Expected back"}
      </p>
      <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-foreground">
        {remaining > 0 ? formatRemaining(remaining) : "Any moment now"}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Scheduled end · {new Date(target).toLocaleString()}
      </p>
    </div>
  );
}

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { roles, loading } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: maintenance } = useQuery({
    queryKey: ["maintenance-config"],
    queryFn: fetchMaintenance,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const isStaff = roles.includes("admin") || roles.includes("super_admin");
  const allowedPath = ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));

  if (!maintenance?.enabled || loading || isStaff || allowedPath) {
    return (
      <>
        {maintenance?.enabled && isStaff ? (
          <div className="bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-amber-950">
            Maintenance mode is ON — only Admins and Super Admins can access Landoo.
            {maintenance.scheduled_end
              ? ` Scheduled to end ${new Date(maintenance.scheduled_end).toLocaleString()}.`
              : ""}
          </div>
        ) : null}
        {children}
      </>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Wrench className="h-7 w-7" />
        </span>
        <h1 className="mt-5 text-2xl font-bold text-foreground">{maintenance.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{maintenance.message}</p>
        {maintenance.scheduled_end ? <Countdown endsAt={maintenance.scheduled_end} /> : null}
        <Link
          to="/login"
          className="mt-6 inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          Staff sign in
        </Link>
      </div>
    </main>
  );
}
